/**
 * Offer Service
 * Abstracts data fetching for offers. Uses real API calls.
 * 
 * Matches real database schema:
 * - offer_assignment_id, ref_user_id, reward_id, display_priority
 * - assignment_strategy (MIGRATION, MANUAL only)
 * - context_json, created_at, updated_at, partner_product_id
 */

import type {
  OfferResponse,
  OfferQueryParams,
  CreateOfferRequest,
  UpdateOfferRequest,
} from '@/app/types/offers';
import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';

export interface IOfferService {
  queryOffers(params: OfferQueryParams): Promise<{ offers: OfferResponse[]; total: number }>;
  getOfferById(id: string): Promise<OfferResponse | null>;
  createOffer(request: CreateOfferRequest): Promise<OfferResponse>;
  updateOffer(offerId: string, request: UpdateOfferRequest): Promise<OfferResponse | null>;
  deleteOffer(offerId: string): Promise<boolean>;
}

const apiOfferService: IOfferService = {
  async queryOffers(params: OfferQueryParams): Promise<{ offers: OfferResponse[]; total: number }> {
    try {
      // Build query params, filtering out undefined/null values
      const queryParams: Record<string, any> = {};
      if (params.user_id !== undefined && params.user_id !== null) {
        queryParams.user_id = parseInt(params.user_id, 10); // Convert string to int for backend
      }
      if (params.reward_id !== undefined && params.reward_id !== null) {
        queryParams.reward_id = params.reward_id;
      }
      if (params.partner_product_id !== undefined && params.partner_product_id !== null) {
        queryParams.partner_product_id = params.partner_product_id;
      }
      if (params.assignment_strategy !== undefined && params.assignment_strategy !== null) {
        queryParams.assignment_strategy = params.assignment_strategy;
      }
      if (params.group_by !== undefined && params.group_by !== null) {
        queryParams.group_by = params.group_by;
      }
      if (params.limit !== undefined && params.limit !== null) {
        queryParams.limit = params.limit;
      }
      if (params.offset !== undefined && params.offset !== null) {
        queryParams.offset = params.offset;
      }

      const response = await api.get<{ offers: OfferResponse[]; total: number; limit: number; offset: number; has_more: boolean; grouped_by?: string }>(API_ENDPOINTS.admin.offers, {
        params: queryParams,
      });
      // Convert backend response to frontend format
      // Backend returns UUIDs and ints, frontend expects strings
      const offers = response.data.offers.map(offer => ({
        ...offer,
        offer_id: offer.offer_id.toString(),
        user_id: offer.user_id.toString(),
        reward_id: offer.reward_id.toString(),
        created_at: offer.created_at.toString(),
        updated_at: offer.updated_at.toString(),
      }));
      return { offers, total: response.data.total };
    } catch (error: any) {
      console.error('Failed to query offers:', error);
      throw error;
    }
  },

  async getOfferById(id: string): Promise<OfferResponse | null> {
    try {
      const response = await api.get<OfferResponse>(`${API_ENDPOINTS.admin.offers}/${id}`);
      // Convert backend response to frontend format
      const offer = response.data;
      return {
        ...offer,
        offer_id: offer.offer_id.toString(),
        user_id: offer.user_id.toString(),
        reward_id: offer.reward_id.toString(),
        created_at: offer.created_at.toString(),
        updated_at: offer.updated_at.toString(),
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to get offer:', error);
      throw error;
    }
  },

  async createOffer(request: CreateOfferRequest): Promise<OfferResponse> {
    try {
      // Convert frontend request to backend format
      const backendRequest = {
        ...request,
        user_id: parseInt(request.user_id, 10), // Convert string to int
        reward_id: request.reward_id, // Already UUID string
      };
      const response = await api.post<OfferResponse>(API_ENDPOINTS.admin.offers, backendRequest);
      // Convert backend response to frontend format
      const offer = response.data;
      return {
        ...offer,
        offer_id: offer.offer_id.toString(),
        user_id: offer.user_id.toString(),
        reward_id: offer.reward_id.toString(),
        created_at: offer.created_at.toString(),
        updated_at: offer.updated_at.toString(),
      };
    } catch (error: any) {
      console.error('Failed to create offer:', error);
      throw error;
    }
  },

  async updateOffer(offerId: string, request: UpdateOfferRequest): Promise<OfferResponse | null> {
    try {
      const response = await api.put<OfferResponse>(`${API_ENDPOINTS.admin.offers}/${offerId}`, request);
      // Convert backend response to frontend format
      const offer = response.data;
      return {
        ...offer,
        offer_id: offer.offer_id.toString(),
        user_id: offer.user_id.toString(),
        reward_id: offer.reward_id.toString(),
        created_at: offer.created_at.toString(),
        updated_at: offer.updated_at.toString(),
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to update offer:', error);
      throw error;
    }
  },

  async deleteOffer(offerId: string): Promise<boolean> {
    try {
      await api.delete(`${API_ENDPOINTS.admin.offers}/${offerId}`);
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false;
      }
      console.error('Failed to delete offer:', error);
      throw error;
    }
  },
};

// Export the real API service
export const offerService: IOfferService = apiOfferService;
export default offerService;

